/**
 * Redux Slice for Payment System
 * Phase 11 - Frontend Implementation
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../axios';
import {
  Invoice,
  InvoiceDetail,
  PaymentStatus,
  UserPaymentStatus,
  PaymentInitiationResponse,
  PaymentStatistics,
  PaginatedResponse,
  CommissionStatsResponse,
  CommissionRate,
  AdminDynamicsResponse,
  AdminDynamicsScope,
  AdminDynamicsTradeType,
  AdminOrphanTradesCleanupResponse,
  PlatformNotificationItem,
} from './types';
import { API_PATHS } from './constants';

// ===== Async Thunks =====

/**
 * Fetch user invoices
 * GET /user-payments/invoices?page=1&limit=20&status=PENDING&sortOrder=desc
 */
export const fetchUserInvoices = createAsyncThunk(
  'payments/fetchUserInvoices',
  async (
    params: { page: number; limit: number; status?: string; sortOrder?: string },
    thunkAPI
  ) => {
    try {
      const response = await axios.get<PaginatedResponse<Invoice>>(
        API_PATHS.USER_INVOICES,
        { params }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to fetch invoices');
    }
  }
);

/**
 * Fetch invoice details with linked trades
 * GET /user-payments/invoices/:invoiceId
 */
export const fetchInvoiceDetail = createAsyncThunk(
  'payments/fetchInvoiceDetail',
  async (invoiceId: string, thunkAPI) => {
    try {
      const response = await axios.get<InvoiceDetail>(
        API_PATHS.USER_INVOICE_DETAIL.replace(':invoiceId', invoiceId)
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to fetch invoice details');
    }
  }
);

/**
 * Get payment status for invoice
 * GET /user-payments/invoices/:invoiceId/payment-status
 */
export const fetchPaymentStatus = createAsyncThunk(
  'payments/fetchPaymentStatus',
  async (invoiceId: string, thunkAPI) => {
    try {
      const response = await axios.get<PaymentStatus>(
        API_PATHS.USER_PAYMENT_STATUS.replace(':invoiceId', invoiceId)
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to fetch payment status');
    }
  }
);

/**
 * Initiate NowPayments payment
 * POST /user-payments/invoices/:invoiceId/pay-with-nowpayments
 * Body: { amount?: number }
 */
export const initiatePayment = createAsyncThunk(
  'payments/initiatePayment',
  async (
    { invoiceId, amount }: { invoiceId: string; amount?: number },
    thunkAPI
  ) => {
    try {
      const payload = typeof amount === 'number' ? { amount } : {};
      const response = await axios.post<PaymentInitiationResponse>(
        API_PATHS.USER_PAYMENT_INITIATE.replace(':invoiceId', invoiceId),
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to initiate payment');
    }
  }
);

/**
 * Fetch payment statistics
 * GET /user-payments/statistics
 */
export const fetchPaymentStatistics = createAsyncThunk(
  'payments/fetchPaymentStatistics',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<PaymentStatistics>(
        API_PATHS.USER_PAYMENT_STATISTICS
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to fetch statistics');
    }
  }
);

/**
 * Download invoice PDF
 * GET /user-payments/invoices/:invoiceId/pdf
 */
export const downloadInvoicePDF = createAsyncThunk(
  'payments/downloadInvoicePDF',
  async (invoiceId: string, thunkAPI) => {
    try {
      const response = await axios.get(
        API_PATHS.USER_INVOICE_PDF.replace(':invoiceId', invoiceId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to download PDF');
    }
  }
);

/**
 * Check user payment status for bot management
 * GET /user-payments/status
 * Returns UserPaymentStatus
 */
export const checkUserPaymentStatus = createAsyncThunk(
  'payments/checkUserPaymentStatus',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<UserPaymentStatus>(
        API_PATHS.USER_PAYMENT_CHECK_STATUS
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to check payment status');
    }
  }
);

/**
 * Fetch admin invoices for all users
 * GET /admin/payments/invoices?page=1&limit=20&status=all&sortBy=periodStartDate&sortOrder=desc
 */
export const fetchAdminInvoices = createAsyncThunk(
  'payments/fetchAdminInvoices',
  async (
    params: {
      page: number;
      limit: number;
      status?: string;
      userEmail?: string;
      userId?: string;
      sortBy?: string;
      sortOrder?: string;
    },
    thunkAPI
  ) => {
    try {
      const response = await axios.get<PaginatedResponse<Invoice>>(
        '/admin/payments/invoices',
        { params }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to fetch admin invoices');
    }
  }
);

/**
 * Delete (deactivate) admin invoice
 * DELETE /admin/payments/invoices/:invoiceId
 * Body: { reason?: string }
 */
export const deleteAdminInvoice = createAsyncThunk(
  'payments/deleteAdminInvoice',
  async (
    params: { invoiceId: string; reason?: string },
    thunkAPI
  ) => {
    try {
      const response = await axios.delete(
        `/admin/payments/invoices/${params.invoiceId}`,
        { data: { reason: params.reason } }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to delete invoice');
    }
  }
);

/**
 * Fetch admin commission stats for all users
 * GET /admin/payments/users/commission-stats?page=1&limit=20
 */
export const fetchAdminCommissionStats = createAsyncThunk(
  'payments/fetchAdminCommissionStats',
  async (
    params: { page: number; limit: number },
    thunkAPI
  ) => {
    try {
      const response = await axios.get<CommissionStatsResponse>(
        '/admin/payments/users/commission-stats',
        { params }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to fetch commission stats');
    }
  }
);

export const fetchAdminNotifications = createAsyncThunk(
  'payments/fetchAdminNotifications',
  async (
    params: {
      page: number;
      limit: number;
      status?: string;
      severity?: string;
      targetUserEmail?: string;
    },
    thunkAPI
  ) => {
    try {
      const response = await axios.get<PaginatedResponse<PlatformNotificationItem>>(
        API_PATHS.ADMIN_NOTIFICATIONS,
        { params }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || 'Failed to fetch notifications'
      );
    }
  }
);

export const createAdminNotification = createAsyncThunk(
  'payments/createAdminNotification',
  async (
    payload: Omit<PlatformNotificationItem, '_id' | 'updatedAt' | 'createdAt'>,
    thunkAPI
  ) => {
    try {
      const response = await axios.post<PlatformNotificationItem>(
        API_PATHS.ADMIN_NOTIFICATIONS,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || 'Failed to create notification'
      );
    }
  }
);

export const updateAdminNotification = createAsyncThunk(
  'payments/updateAdminNotification',
  async (
    params: {
      notificationId: string;
      data: Omit<PlatformNotificationItem, '_id' | 'updatedAt' | 'createdAt'>;
    },
    thunkAPI
  ) => {
    try {
      const response = await axios.put<PlatformNotificationItem>(
        API_PATHS.ADMIN_NOTIFICATIONS + `/${params.notificationId}`,
        params.data
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || 'Failed to update notification'
      );
    }
  }
);

export const publishAdminNotification = createAsyncThunk(
  'payments/publishAdminNotification',
  async (notificationId: string, thunkAPI) => {
    try {
      const response = await axios.post<PlatformNotificationItem>(
        API_PATHS.ADMIN_NOTIFICATION_PUBLISH.replace(':notificationId', notificationId)
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || 'Failed to publish notification'
      );
    }
  }
);

export const unpublishAdminNotification = createAsyncThunk(
  'payments/unpublishAdminNotification',
  async (notificationId: string, thunkAPI) => {
    try {
      const response = await axios.post<PlatformNotificationItem>(
        API_PATHS.ADMIN_NOTIFICATION_UNPUBLISH.replace(
          ':notificationId',
          notificationId
        )
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || 'Failed to unpublish notification'
      );
    }
  }
);

export const deleteAdminNotification = createAsyncThunk(
  'payments/deleteAdminNotification',
  async (notificationId: string, thunkAPI) => {
    try {
      await axios.delete(`${API_PATHS.ADMIN_NOTIFICATIONS}/${notificationId}`);
      return notificationId;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || 'Failed to delete notification'
      );
    }
  }
);

/**
 * Fetch admin trading dynamics (single/group/all users)
 * GET /admin/analytics/bot-dynamics
 */
export const fetchAdminBotDynamics = createAsyncThunk(
  'payments/fetchAdminBotDynamics',
  async (
    params: {
      scope: AdminDynamicsScope;
      tradeType: AdminDynamicsTradeType;
      page: number;
      limit: number;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      userIds?: string[];
      groupId?: string;
      sortBy?: 'todayClosedPnl' | 'periodClosedPnl' | 'allTimeClosedPnl' | 'botsTotal' | 'botsRunning';
      sortOrder?: 'asc' | 'desc';
    },
    thunkAPI
  ) => {
    try {
      const query: Record<string, string | number> = {
        scope: params.scope,
        tradeType: params.tradeType,
        page: params.page,
        limit: params.limit,
      };
      if (params.search?.trim()) query.search = params.search.trim();
      if (params.dateFrom) query.dateFrom = params.dateFrom;
      if (params.dateTo) query.dateTo = params.dateTo;
      if (params.groupId?.trim()) query.groupId = params.groupId.trim();
      if (params.sortBy) query.sortBy = params.sortBy;
      if (params.sortOrder) query.sortOrder = params.sortOrder;
      if (params.userIds?.length) {
        query.userIds = params.userIds
          .map((id) => id.trim())
          .filter(Boolean)
          .join(',');
      }

      const response = await axios.get<AdminDynamicsResponse>(
        API_PATHS.ADMIN_BOT_DYNAMICS,
        { params: query }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to fetch bot dynamics'
      );
    }
  }
);

/**
 * Preview orphan trades cleanup for deleted bots of a user
 * POST /admin/analytics/users/:userId/orphan-trades-cleanup
 * Body: { dryRun: true, batchLimit?: number }
 */
export const previewAdminOrphanTradesCleanup = createAsyncThunk(
  'payments/previewAdminOrphanTradesCleanup',
  async (
    params: { userId: string; batchLimit?: number },
    thunkAPI
  ) => {
    try {
      const response = await axios.post<AdminOrphanTradesCleanupResponse>(
        API_PATHS.ADMIN_ORPHAN_TRADES_CLEANUP.replace(':userId', params.userId),
        { dryRun: true, batchLimit: params.batchLimit }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to preview orphan trades cleanup'
      );
    }
  }
);

/**
 * Execute orphan trades cleanup for deleted bots of a user
 * POST /admin/analytics/users/:userId/orphan-trades-cleanup
 * Body: { dryRun: false, batchLimit?: number }
 */
export const executeAdminOrphanTradesCleanup = createAsyncThunk(
  'payments/executeAdminOrphanTradesCleanup',
  async (
    params: { userId: string; batchLimit?: number },
    thunkAPI
  ) => {
    try {
      const response = await axios.post<AdminOrphanTradesCleanupResponse>(
        API_PATHS.ADMIN_ORPHAN_TRADES_CLEANUP.replace(':userId', params.userId),
        { dryRun: false, batchLimit: params.batchLimit }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to execute orphan trades cleanup'
      );
    }
  }
);

type CommissionRateApi = Omit<CommissionRate, 'commissionRateId' | 'percentage'> & {
  commissionRateId?: string | null;
  percentage?: number;
  commissionPercent?: number;
};

interface CommissionRatesResponseApi {
  data: CommissionRateApi[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  page?: number;
  limit?: number;
  total?: number;
  pages?: number;
}

const normalizeCommissionRateValue = (
  value: number | undefined,
  source: 'fraction' | 'percent'
): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  if (source === 'percent') return value / 100;
  return value > 1 ? value / 100 : value;
};

const normalizeCommissionRate = (rate: CommissionRateApi): CommissionRate => {
  const normalizedPercentage =
    typeof rate.commissionPercent === 'number'
      ? normalizeCommissionRateValue(rate.commissionPercent, 'percent')
      : normalizeCommissionRateValue(rate.percentage, 'fraction');

  return {
    commissionRateId: rate.commissionRateId ?? rate.userId,
    userId: rate.userId,
    userEmail: rate.userEmail,
    percentage: normalizedPercentage,
    effectiveFrom: rate.effectiveFrom,
    notes: rate.notes,
  };
};

const normalizeCommissionRatesResponse = (
  response: CommissionRatesResponseApi
): PaginatedResponse<CommissionRate> => {
  const data = Array.isArray(response.data)
    ? response.data.map(normalizeCommissionRate)
    : [];

  const pagination = response.pagination ?? {
    page: response.page ?? 1,
    limit: response.limit ?? data.length,
    total: response.total ?? data.length,
    pages: response.pages ?? 1,
  };

  return { data, pagination };
};

/**
 * Fetch all users with their current commission rates (for AdminCommissionManager)
 * GET /api/admin/payments/users/commission-rates?page=1&limit=20
 */
export const fetchAdminCommissionRates = createAsyncThunk(
  'payments/fetchAdminCommissionRates',
  async (
    params: { page: number; limit: number; search?: string },
    thunkAPI
  ) => {
    try {
      const response = await axios.get<CommissionRatesResponseApi>(
        '/admin/payments/users/commission-rates',
        { params }
      );
      return normalizeCommissionRatesResponse(response.data);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || 'Failed to fetch commission rates');
    }
  }
);

/**
 * Update user commission rate
 * PUT /admin/payments/users/:userId/commission-rate
 */
export const updateAdminCommissionRate = createAsyncThunk(
  'payments/updateAdminCommissionRate',
  async (
    params: { userId: string; percentage: number },
    thunkAPI
  ) => {
    try {
      const response = await axios.put<CommissionRateApi>(
        `/admin/payments/users/${params.userId}/commission-rate`,
        { commissionPercent: params.percentage }
      );
      return normalizeCommissionRate(response.data);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update commission rate');
    }
  }
);

// ===== State Interface =====

interface PaymentsState {
  // Invoices List
  invoices: Invoice[];
  invoicesLoading: boolean;
  invoicesError: string | null;
  invoicesPagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };

  // Invoice Detail
  selectedInvoice: InvoiceDetail | null;
  selectedInvoiceLoading: boolean;
  selectedInvoiceError: string | null;

  // Payment Status
  paymentStatus: PaymentStatus | null;
  paymentStatusLoading: boolean;
  paymentStatusError: string | null;

  // Payment Initiation
  paymentInitiating: boolean;
  paymentInitiationError: string | null;
  paymentLink: string | null;
  paymentInvoiceId: string | null;

  // Statistics
  statistics: PaymentStatistics | null;
  statisticsLoading: boolean;
  statisticsError: string | null;

  // PDF Download
  pdfDownloading: boolean;
  pdfError: string | null;

  // User Payment Status (for bot management)
  userPaymentStatus: UserPaymentStatus | null;
  userPaymentStatusLoading: boolean;
  userPaymentStatusError: string | null;

  // Admin Notifications
  adminNotifications: PlatformNotificationItem[];
  adminNotificationsLoading: boolean;
  adminNotificationsError: string | null;
  adminNotificationsPagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  adminNotificationMutating: boolean;
  adminNotificationMutationError: string | null;

  // Admin Commission Stats
  commissionStats: CommissionStatsResponse | null;
  commissionStatsLoading: boolean;
  commissionStatsError: string | null;

  // Admin Commission Rates (for AdminCommissionManager)
  commissionRates: CommissionRate[];
  commissionRatesLoading: boolean;
  commissionRatesError: string | null;
  commissionRatesPagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };

  // Admin Commission Rate Update
  commissionRateUpdating: boolean;
  commissionRateUpdateError: string | null;

  // Admin Bot Dynamics
  adminBotDynamics: AdminDynamicsResponse | null;
  adminBotDynamicsLoading: boolean;
  adminBotDynamicsError: string | null;
}

const initialState: PaymentsState = {
  invoices: [],
  invoicesLoading: false,
  invoicesError: null,
  invoicesPagination: { page: 1, limit: 20, total: 0, pages: 0 },

  selectedInvoice: null,
  selectedInvoiceLoading: false,
  selectedInvoiceError: null,

  paymentStatus: null,
  paymentStatusLoading: false,
  paymentStatusError: null,

  paymentInitiating: false,
  paymentInitiationError: null,
  paymentLink: null,
  paymentInvoiceId: null,

  statistics: null,
  statisticsLoading: false,
  statisticsError: null,

  pdfDownloading: false,
  pdfError: null,

  userPaymentStatus: null,
  userPaymentStatusLoading: false,
  userPaymentStatusError: null,

  adminNotifications: [],
  adminNotificationsLoading: false,
  adminNotificationsError: null,
  adminNotificationsPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  adminNotificationMutating: false,
  adminNotificationMutationError: null,

  commissionStats: null,
  commissionStatsLoading: false,
  commissionStatsError: null,

  commissionRates: [],
  commissionRatesLoading: false,
  commissionRatesError: null,
  commissionRatesPagination: { page: 1, limit: 20, total: 0, pages: 0 },

  commissionRateUpdating: false,
  commissionRateUpdateError: null,

  adminBotDynamics: null,
  adminBotDynamicsLoading: false,
  adminBotDynamicsError: null,
};

// ===== Slice =====

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    // Clear errors
    clearInvoicesError: (state) => {
      state.invoicesError = null;
    },
    clearSelectedInvoiceError: (state) => {
      state.selectedInvoiceError = null;
    },
    clearPaymentError: (state) => {
      state.paymentInitiationError = null;
    },
    clearPaymentLink: (state) => {
      state.paymentLink = null;
      state.paymentInvoiceId = null;
    },

    // Reset payment initiation
    resetPaymentInitiation: (state) => {
      state.paymentInitiating = false;
      state.paymentInitiationError = null;
      state.paymentLink = null;
      state.paymentInvoiceId = null;
    },

    // Update selected invoice
    setSelectedInvoice: (state, action: PayloadAction<InvoiceDetail | null>) => {
      state.selectedInvoice = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch User Invoices
    builder
      .addCase(fetchUserInvoices.pending, (state) => {
        state.invoicesLoading = true;
        state.invoicesError = null;
      })
      .addCase(fetchUserInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        state.invoices = action.payload.data;
        state.invoicesPagination = action.payload.pagination;
      })
      .addCase(fetchUserInvoices.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.invoicesError = action.payload as string;
      });

    // Fetch Admin Invoices
    builder
      .addCase(fetchAdminInvoices.pending, (state) => {
        state.invoicesLoading = true;
        state.invoicesError = null;
      })
      .addCase(fetchAdminInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        state.invoices = action.payload.data;
        state.invoicesPagination = action.payload.pagination;
      })
      .addCase(fetchAdminInvoices.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.invoicesError = action.payload as string;
      });

    // Fetch Invoice Detail
    builder
      .addCase(fetchInvoiceDetail.pending, (state) => {
        state.selectedInvoiceLoading = true;
        state.selectedInvoiceError = null;
        state.selectedInvoice = null;
      })
      .addCase(fetchInvoiceDetail.fulfilled, (state, action) => {
        state.selectedInvoiceLoading = false;
        state.selectedInvoice = action.payload;
      })
      .addCase(fetchInvoiceDetail.rejected, (state, action) => {
        state.selectedInvoiceLoading = false;
        state.selectedInvoiceError = action.payload as string;
      });

    // Fetch Payment Status
    builder
      .addCase(fetchPaymentStatus.pending, (state) => {
        state.paymentStatusLoading = true;
        state.paymentStatusError = null;
      })
      .addCase(fetchPaymentStatus.fulfilled, (state, action) => {
        state.paymentStatusLoading = false;
        state.paymentStatus = action.payload;
      })
      .addCase(fetchPaymentStatus.rejected, (state, action) => {
        state.paymentStatusLoading = false;
        state.paymentStatusError = action.payload as string;
      });

    // Initiate Payment
    builder
      .addCase(initiatePayment.pending, (state) => {
        state.paymentInitiating = true;
        state.paymentInitiationError = null;
      })
      .addCase(initiatePayment.fulfilled, (state, action) => {
        state.paymentInitiating = false;
        state.paymentLink = action.payload.paymentLink;
        state.paymentInvoiceId = action.payload.invoiceId ?? action.meta.arg.invoiceId;
      })
      .addCase(initiatePayment.rejected, (state, action) => {
        state.paymentInitiating = false;
        state.paymentInitiationError = action.payload as string;
      });

    // Fetch Statistics
    builder
      .addCase(fetchPaymentStatistics.pending, (state) => {
        state.statisticsLoading = true;
        state.statisticsError = null;
      })
      .addCase(fetchPaymentStatistics.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchPaymentStatistics.rejected, (state, action) => {
        state.statisticsLoading = false;
        state.statisticsError = action.payload as string;
      });

    // Download PDF
    builder
      .addCase(downloadInvoicePDF.pending, (state) => {
        state.pdfDownloading = true;
        state.pdfError = null;
      })
      .addCase(downloadInvoicePDF.fulfilled, (state) => {
        state.pdfDownloading = false;
      })
      .addCase(downloadInvoicePDF.rejected, (state, action) => {
        state.pdfDownloading = false;
        state.pdfError = action.payload as string;
      });

    // Check User Payment Status (for bot management)
    builder
      .addCase(checkUserPaymentStatus.pending, (state) => {
        state.userPaymentStatusLoading = true;
        state.userPaymentStatusError = null;
      })
      .addCase(checkUserPaymentStatus.fulfilled, (state, action) => {
        state.userPaymentStatusLoading = false;
        state.userPaymentStatus = action.payload;
      })
      .addCase(checkUserPaymentStatus.rejected, (state, action) => {
        state.userPaymentStatusLoading = false;
        state.userPaymentStatusError = action.payload as string;
      });

    builder
      .addCase(fetchAdminNotifications.pending, (state) => {
        state.adminNotificationsLoading = true;
        state.adminNotificationsError = null;
      })
      .addCase(fetchAdminNotifications.fulfilled, (state, action) => {
        state.adminNotificationsLoading = false;
        state.adminNotifications = action.payload.data;
        state.adminNotificationsPagination = action.payload.pagination;
      })
      .addCase(fetchAdminNotifications.rejected, (state, action) => {
        state.adminNotificationsLoading = false;
        state.adminNotificationsError = action.payload as string;
      });

    builder
      .addCase(createAdminNotification.pending, (state) => {
        state.adminNotificationMutating = true;
        state.adminNotificationMutationError = null;
      })
      .addCase(createAdminNotification.fulfilled, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotifications = [action.payload, ...state.adminNotifications];
      })
      .addCase(createAdminNotification.rejected, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotificationMutationError = action.payload as string;
      })
      .addCase(updateAdminNotification.pending, (state) => {
        state.adminNotificationMutating = true;
        state.adminNotificationMutationError = null;
      })
      .addCase(updateAdminNotification.fulfilled, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotifications = state.adminNotifications.map((item) =>
          item._id === action.payload._id ? action.payload : item
        );
      })
      .addCase(updateAdminNotification.rejected, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotificationMutationError = action.payload as string;
      })
      .addCase(publishAdminNotification.pending, (state) => {
        state.adminNotificationMutating = true;
        state.adminNotificationMutationError = null;
      })
      .addCase(publishAdminNotification.fulfilled, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotifications = state.adminNotifications.map((item) =>
          item._id === action.payload._id ? action.payload : item
        );
      })
      .addCase(publishAdminNotification.rejected, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotificationMutationError = action.payload as string;
      })
      .addCase(unpublishAdminNotification.pending, (state) => {
        state.adminNotificationMutating = true;
        state.adminNotificationMutationError = null;
      })
      .addCase(unpublishAdminNotification.fulfilled, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotifications = state.adminNotifications.map((item) =>
          item._id === action.payload._id ? action.payload : item
        );
      })
      .addCase(unpublishAdminNotification.rejected, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotificationMutationError = action.payload as string;
      })
      .addCase(deleteAdminNotification.pending, (state) => {
        state.adminNotificationMutating = true;
        state.adminNotificationMutationError = null;
      })
      .addCase(deleteAdminNotification.fulfilled, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotifications = state.adminNotifications.filter(
          (item) => item._id !== action.payload
        );
      })
      .addCase(deleteAdminNotification.rejected, (state, action) => {
        state.adminNotificationMutating = false;
        state.adminNotificationMutationError = action.payload as string;
      });

    // Fetch Admin Commission Stats
    builder
      .addCase(fetchAdminCommissionStats.pending, (state) => {
        state.commissionStatsLoading = true;
        state.commissionStatsError = null;
      })
      .addCase(fetchAdminCommissionStats.fulfilled, (state, action) => {
        state.commissionStatsLoading = false;
        state.commissionStats = action.payload;
      })
      .addCase(fetchAdminCommissionStats.rejected, (state, action) => {
        state.commissionStatsLoading = false;
        state.commissionStatsError = action.payload as string;
      });

    // Fetch Admin Commission Rates
    builder
      .addCase(fetchAdminCommissionRates.pending, (state) => {
        state.commissionRatesLoading = true;
        state.commissionRatesError = null;
      })
      .addCase(fetchAdminCommissionRates.fulfilled, (state, action) => {
        state.commissionRatesLoading = false;
        state.commissionRates = action.payload.data;
        state.commissionRatesPagination = action.payload.pagination;
      })
      .addCase(fetchAdminCommissionRates.rejected, (state, action) => {
        state.commissionRatesLoading = false;
        state.commissionRatesError = action.payload as string;
      });

    // Fetch Admin Bot Dynamics
    builder
      .addCase(fetchAdminBotDynamics.pending, (state) => {
        state.adminBotDynamicsLoading = true;
        state.adminBotDynamicsError = null;
      })
      .addCase(fetchAdminBotDynamics.fulfilled, (state, action) => {
        state.adminBotDynamicsLoading = false;
        state.adminBotDynamics = action.payload;
      })
      .addCase(fetchAdminBotDynamics.rejected, (state, action) => {
        state.adminBotDynamicsLoading = false;
        state.adminBotDynamicsError = action.payload as string;
      });

    // Update Admin Commission Rate
    builder
      .addCase(updateAdminCommissionRate.pending, (state) => {
        state.commissionRateUpdating = true;
        state.commissionRateUpdateError = null;
      })
      .addCase(updateAdminCommissionRate.fulfilled, (state, action) => {
        state.commissionRateUpdating = false;
        // Update the rate in the list
        const index = state.commissionRates.findIndex(
          (r) => r.commissionRateId === action.payload.commissionRateId
        );
        if (index !== -1) {
          state.commissionRates[index] = action.payload;
        }
      })
      .addCase(updateAdminCommissionRate.rejected, (state, action) => {
        state.commissionRateUpdating = false;
        state.commissionRateUpdateError = action.payload as string;
      });

    // Delete Admin Invoice
    builder
      .addCase(deleteAdminInvoice.pending, (state) => {
        state.invoicesLoading = true;
        state.invoicesError = null;
      })
      .addCase(deleteAdminInvoice.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        // Remove the deleted invoice from the list or refresh
      })
      .addCase(deleteAdminInvoice.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.invoicesError = action.payload as string;
      });
  },
});

export const {
  clearInvoicesError,
  clearSelectedInvoiceError,
  clearPaymentError,
  clearPaymentLink,
  resetPaymentInitiation,
  setSelectedInvoice,
} = paymentsSlice.actions;

export default paymentsSlice.reducer;
